import { ProductImage } from 'components/ProductTemplates';
import { ReactNode, useContext, useMemo } from 'react';
import { AppContext } from 'context/ContextProvider';
import { buildDisplayText } from 'utils';
import { DisplayTextKeys } from 'views/Trade/types';
import { useTranslation } from 'react-i18next';
import { Button } from 'components';
import { useNavigate } from 'react-router-dom';
import { NavigationPath } from 'types/DomainTypes';
import { AccountViewType, SubjectOptionKeys } from 'views/Accounts/types';
import { getRegionColors } from 'helpers';

export interface IWineInfoTemplate {
  config?: {
    wineName?: string;
    lwin18?: string;
    vintage?: number;
    quantity?: number;
    marketValue?: number;
    unitSize?: string;
    cultWinesAllocationRegion?: string;
    showTemplate?: boolean;
    removeWineNameAndImage?: boolean;
    region?: string;
    displayText?: {
      leftDisplayText?: DisplayTextKeys;
      rightDisplayText?: DisplayTextKeys;
    };
  };
  children?: ReactNode;
}

const WineInfoTemplate = ({ config, children }: IWineInfoTemplate) => {
  const { formatter } = useContext(AppContext);
  const wineName = config?.wineName;
  const lwin18 = config?.lwin18;
  const quantity = config?.quantity;
  const vintage = config?.vintage;
  const marketValue = config?.marketValue;
  const unitSize = config?.unitSize;
  const region = config?.region;
  const showTemplate = config?.showTemplate !== undefined ? config?.showTemplate : true;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'trade:common', t), [t]);
  const text = config?.displayText;
  const removeWineNameAndImage = config?.removeWineNameAndImage;
  const cultWinesAllocationRegion = config?.cultWinesAllocationRegion;
  const regionColor = useMemo(() => {
    return getRegionColors(cultWinesAllocationRegion ?? '').color;
  }, [cultWinesAllocationRegion]);

  return (
    <div className="flex flex-1 flex-col bg-white dark:bg-slate-900">
      {showTemplate && (
        <>
          {!removeWineNameAndImage && (
            <>
              <ProductImage imageByLwin18={lwin18} region={region} bgColor={regionColor} />
              <div className="px-4 pt-3 pb-2">
                <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white leading-snug">{`${vintage} ${wineName}`}</h2>
              </div>
            </>
          )}

          {/* Stats Card */}
          <div className="mx-4 my-2">
            <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-3 border border-gray-100 dark:border-slate-700/50 shadow-sm dark:shadow-slate-950/30">
              <div className="flex justify-center divide-x divide-gray-200 dark:divide-slate-700">
                <div className="flex flex-col items-center flex-1 px-3">
                  <span className="text-[10px] text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-0.5">
                    {text?.leftDisplayText
                      ? displayText[text?.leftDisplayText]
                      : displayText[DisplayTextKeys.UINTS_OWNED]}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-white font-semibold">
                    {quantity}x({unitSize})
                  </span>
                </div>

                <div className="flex flex-col items-center flex-1 px-3">
                  <span className="text-[10px] text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-0.5">
                    {text?.rightDisplayText
                      ? displayText[text?.rightDisplayText]
                      : displayText[DisplayTextKeys.VALUE_PER_UNIT]}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-white font-semibold">
                    {formatter.format(marketValue || 0, true)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className={`flex-col flex flex-1 bg-white dark:bg-slate-900 ${!showTemplate ? '' : ''}`}>{children}</div>

      {showTemplate && (
        <div className="px-4 pb-4">
          <Button
            className="btn-link w-full flex justify-center text-center text-sm text-orange dark:text-teal-400 hover:text-orange/80 dark:hover:text-teal-300 transition-colors"
            type="button"
            isLink={true}
            onClick={() => {
              navigate(`${NavigationPath.ACCOUNTS}`, {
                state: { accountViewType: AccountViewType.CONTACT_US, subject: SubjectOptionKeys.GENERAL },
              });
            }}
          >
            {displayText[DisplayTextKeys.CONTACT_US_INFO]}
          </Button>
        </div>
      )}
    </div>
  );
};

export default WineInfoTemplate;
